'use client';

import React, { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import {
  Search,
  Plus,
  User,
  ArrowLeft,
  ExternalLink,
  Check,
  LogOut,
  Star,
  ChevronRight,
  Sparkles,
  MapPin,
  X,
  Clock,
  Phone
} from 'lucide-react';
import { CalenddyLogo } from '@/components/brand/CalenddyLogo';
import { Service, Category, Staff } from './types';
import { getThemeTokens } from '@/lib/themeTokens';

function slugify(text: string): string {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

interface BranchItem {
  id: string;
  name: string;
  slug: string;
  address?: string | null;
  googleMapsUrl?: string | null;
  phone?: string | null;
  imageUrl?: string | null;
  coverUrl?: string | null;
  active: boolean;
  isMain?: boolean;
}

interface DirectBranchCatalogViewProps {
  business: any;
  currentBranch?: BranchItem | null;
  allBranches?: BranchItem[];
  hasMultipleBranches?: boolean;
  categories: Category[];
  services: Service[];
  staffList: Staff[];
  selectedStaffId: string;
  onSelectStaff: (staffId: string) => void;
  onSelectService: (service: Service) => void;
  onOpenMyBookings: () => void;
}

export function DirectBranchCatalogView({
  business,
  currentBranch,
  allBranches = [],
  hasMultipleBranches = false,
  categories,
  services,
  staffList,
  selectedStaffId,
  onSelectStaff,
  onSelectService,
  onOpenMyBookings
}: DirectBranchCatalogViewProps) {
  const theme = getThemeTokens(business?.portalTheme, business?.brandColor);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [showLocationModal, setShowLocationModal] = useState(false);

  // Identidad
  const businessName = business?.name || 'Calenddy';
  const branchName = currentBranch?.name || businessName;
  const initialLetter = businessName.trim().charAt(0).toUpperCase() || 'C';

  // Portada: prioridad branch.coverUrl -> business.coverUrl -> branch.imageUrl
  const coverUrl = currentBranch?.coverUrl || business?.coverUrl || currentBranch?.imageUrl;
  const logoUrl = business?.logoUrl;

  const activeStaff = useMemo(() => {
    if (!selectedStaffId || selectedStaffId === 'any') return null;
    return staffList.find((s) => s.id === selectedStaffId) || null;
  }, [selectedStaffId, staffList]);

  const hasFeaturedService = useMemo(() => services.some((s) => s.isFeatured), [services]);

  // Lista de categorías únicas de los servicios de esta sucursal (o del profesional activo).
  // No incluye 'Destacados' ni 'General', solo las categorías reales (Corte, Alisados, Keratina, etc.)
  const displayCategories = useMemo(() => {
    const relevantServices = activeStaff
      ? services.filter((service) => {
          if (!service.staff || service.staff.length === 0) return true;
          return service.staff.some((s) => s.id === activeStaff.id);
        })
      : services;

    const branchCatNames = new Set(
      relevantServices
        .map((s) => s.category)
        .filter((name): name is string => {
          if (!name) return false;
          const lower = name.toLowerCase().trim();
          return lower !== 'general' && lower !== 'destacados';
        })
    );

    const fromProps = categories.filter(
      (c) =>
        c.active !== false &&
        branchCatNames.has(c.name) &&
        c.name.toLowerCase().trim() !== 'general' &&
        c.name.toLowerCase().trim() !== 'destacados'
    );
    const sortedFromProps = [...fromProps].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    const matchedNames = new Set(sortedFromProps.map((c) => c.name));

    const remaining = Array.from(branchCatNames)
      .filter((name) => !matchedNames.has(name))
      .map((name) => ({ id: name, name }));

    return [...sortedFromProps, ...remaining];
  }, [categories, services, activeStaff]);

  useEffect(() => {
    if (displayCategories.length > 0) {
      const hasSelected = displayCategories.some(
        (c) => c.id === selectedCategoryId || c.name === selectedCategoryId
      );
      if (!hasSelected) {
        setSelectedCategoryId(displayCategories[0].id);
      }
    }
  }, [displayCategories, selectedCategoryId]);

  // Scrollspy: Sincronizar píldora activa según la categoría que esté visible
  useEffect(() => {
    if (displayCategories.length === 0) return;

    const handleScroll = () => {
      const scrollPos = window.scrollY + 170;
      for (let i = displayCategories.length - 1; i >= 0; i--) {
        const cat = displayCategories[i];
        const el = document.getElementById(`category-${slugify(cat.name)}`);
        if (el && el.offsetTop <= scrollPos) {
          setSelectedCategoryId(cat.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [displayCategories]);

  // Selección y deselección de profesional sincronizado con URL y estado
  const handleSelectStaffMember = (stId: string) => {
    setSearchQuery('');
    onSelectStaff(stId);
    const targetStaff = staffList.find((s) => s.id === stId);
    if (targetStaff && typeof window !== 'undefined') {
      const staffSlug = slugify(targetStaff.name);
      const basePath = currentBranch?.slug
        ? `/${business.slug}/${currentBranch.slug}`
        : `/${business.slug}`;
      window.history.pushState({ staffId: stId }, '', `${basePath}/${staffSlug}`);
    }
  };

  const handleClearStaffMember = () => {
    setSearchQuery('');
    onSelectStaff('any');
    if (typeof window !== 'undefined') {
      const basePath = currentBranch?.slug
        ? `/${business.slug}/${currentBranch.slug}`
        : `/${business.slug}`;
      window.history.pushState({ staffId: 'any' }, '', basePath);
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      if (typeof window === 'undefined') return;
      const segments = window.location.pathname.split('/').filter(Boolean);
      const hasBranch = currentBranch?.slug ? 2 : 1;
      if (segments.length > hasBranch) {
        const staffSlug = segments[hasBranch];
        const match = staffList.find((s) => slugify(s.name) === staffSlug || s.id === staffSlug);
        if (match) {
          onSelectStaff(match.id);
          return;
        }
      }
      onSelectStaff('any');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [staffList, currentBranch, business, onSelectStaff]);

  // Filtrado de servicios por búsqueda y profesional
  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      // Filtro por búsqueda
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesName = service.name.toLowerCase().includes(q);
        const matchesDesc = (service.description || '').toLowerCase().includes(q);
        const matchesCat = (service.category || '').toLowerCase().includes(q);
        if (!matchesName && !matchesDesc && !matchesCat) return false;
      }

      // Filtro por profesional asignado
      if (selectedStaffId && selectedStaffId !== 'any') {
        const hasStaff = !service.staff?.length || service.staff.some((s) => s.id === selectedStaffId);
        if (!hasStaff) return false;
      }

      return true;
    });
  }, [services, searchQuery, selectedStaffId]);

  // Agrupar servicios por categoría preservando el orden de las categorías de la sucursal
  const groupedServices = useMemo(() => {
    const groups: { [key: string]: Service[] } = {};

    displayCategories.forEach((cat) => {
      groups[cat.name] = [];
    });

    filteredServices.forEach((service) => {
      const catName = service.category;
      if (catName && groups[catName]) {
        groups[catName].push(service);
      } else if (displayCategories.length > 0) {
        // Asignar a la primera categoría disponible si no coincide o es nula
        groups[displayCategories[0].name].push(service);
      }
    });

    const orderedResult: { [key: string]: Service[] } = {};
    Object.keys(groups).forEach((name) => {
      if (groups[name].length > 0) {
        orderedResult[name] = groups[name];
      }
    });

    return orderedResult;
  }, [filteredServices, displayCategories]);

  // Servicios destacados y recomendados para la sección Destacados (solo si fue marcado como isFeatured por el comercio)
  const featuredService = useMemo(() => {
    return filteredServices.find((s) => s.isFeatured) || services.find((s) => s.isFeatured);
  }, [filteredServices, services]);

  const recommendedServices = useMemo(() => {
    if (!featuredService) return [];
    const featuredNorm = featuredService.name.toLowerCase().trim();
    const candidateList = filteredServices.length > 0 ? filteredServices : services;

    const seenNames = new Set<string>([featuredNorm]);
    const uniqueRest: Service[] = [];

    for (const s of candidateList) {
      if (s.id === featuredService.id) continue;
      const norm = s.name.toLowerCase().trim();
      if (!seenNames.has(norm)) {
        seenNames.add(norm);
        uniqueRest.push(s);
      }
    }
    return uniqueRest.slice(0, 4);
  }, [filteredServices, featuredService, services]);

  const formatDuration = (mins: number) => {
    if (!mins) return '~30 min';
    if (mins === 60) return '~1 hs';
    if (mins % 60 === 0 && mins > 60) return `~${mins / 60} hs`;
    return `~${mins} min`;
  };

  const formatPrice = (service: Service) => {
    const p = service.cashPrice || service.price || 0;
    if (p <= 0) return 'A Consultar';
    return `Desde ${new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(p)}`;
  };

  return (
    <div className="min-h-screen flex flex-col font-jakarta bg-white text-[#111827] selection:bg-slate-200">
      {/* 1. Header Integrado de Sucursal (Fiel a Calendico Cerini: ← Cambiar sucursal + Mi perfil + Salir) */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 px-4 sm:px-8 py-3">
        <nav className="max-w-[1240px] mx-auto flex items-center justify-between" aria-label="Global">
          {/* Lado Izquierdo: ← Cambiar profesional (si hay activeStaff) o ← Cambiar sucursal */}
          <div className="flex items-center gap-2">
            {activeStaff ? (
              <button
                type="button"
                onClick={handleClearStaffMember}
                style={{
                  fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                  fontSize: '15px',
                  fontWeight: 400,
                  color: '#111827'
                }}
                className="inline-flex items-center gap-2 hover:text-black transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-5 h-5 text-[#111827]" />
                <span>Cambiar profesional</span>
              </button>
            ) : hasMultipleBranches ? (
              <Link
                href={`/${business.slug}`}
                style={{
                  fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                  fontSize: '15px',
                  fontWeight: 400,
                  color: '#111827'
                }}
                className="inline-flex items-center gap-2 hover:text-black transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-[#111827]" />
                <span>Cambiar sucursal</span>
              </Link>
            ) : (
              <Link
                href="/"
                style={{
                  fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                  fontSize: '15px',
                  fontWeight: 400,
                  color: '#111827'
                }}
                className="inline-flex items-center gap-2 hover:text-black transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-[#111827]" />
                <CalenddyLogo size="xs" variant="dark" />
              </Link>
            )}
          </div>

          {/* Lado Derecho: Botón Mi perfil + Botón Cerrar sesión */}
          <div className="flex items-center gap-2">
            <button
              onClick={onOpenMyBookings}
              style={{
                fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                fontSize: '14px',
                fontWeight: 500
              }}
              className="bg-[#111827] hover:bg-black text-white h-10 px-3.5 rounded-xl flex items-center gap-2 transition cursor-pointer shadow-xs whitespace-nowrap"
            >
              <User className="w-4 h-4 text-white" />
              <span>Mi perfil</span>
            </button>

            <button
              onClick={() => {
                if (hasMultipleBranches) {
                  window.location.href = `/${business.slug}`;
                } else {
                  window.location.href = '/';
                }
              }}
              className="h-10 px-3 sm:px-3.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 flex items-center gap-2 text-gray-700 transition cursor-pointer shadow-2xs shrink-0"
              title="Cerrar sesión"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline text-sm font-medium">Cerrar sesión</span>
            </button>
          </div>
        </nav>
      </header>

      {/* 2. Portada de la Sucursal (Proporción exacta e idéntica a la del perfil: h-52 sm:h-64 md:h-76) */}
      <div className="relative w-full overflow-hidden bg-gray-100">
        {coverUrl ? (
          <div className="h-52 sm:h-64 md:h-76 w-full relative">
            <img
              src={coverUrl}
              alt={branchName}
              className="w-full h-full object-cover object-center"
            />
          </div>
        ) : (
          <div
            style={{
              backgroundColor: business?.brandColor || '#111827',
              backgroundImage: `linear-gradient(135deg, ${business?.brandColor || '#111827'} 0%, rgba(0, 0, 0, 0.35) 100%)`
            }}
            className="h-44 sm:h-56 md:h-64 w-full relative border-b border-gray-100/10"
          />
        )}
      </div>

      {/* 3. Hoja Blanca Unificada: Superpuesta a la portada con esquinas redondeadas exactas */}
      <main className="max-w-[1310px] mx-auto w-full relative -mt-5 sm:-mt-6 z-10 bg-white rounded-t-[28px] sm:rounded-t-3xl px-4 sm:px-6 lg:px-8 pb-20 flex-1 space-y-6 sm:space-y-8">
        
        {/* Cabecera: Perfil de Profesional (si está seleccionado) o Cabecera de Sede (si general) */}
        {activeStaff ? (
          <div className="pt-4 sm:pt-6 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-4 sm:gap-6">
              {/* Avatar Cuadrado con Bordes Redondeados del Profesional + Badge Circular de la Marca abajo a la derecha */}
              <div className="relative shrink-0">
                <div className="h-24 w-24 sm:h-32 sm:w-32 md:h-36 md:w-36 rounded-3xl overflow-hidden ring-4 ring-white shadow-sm bg-gray-100 flex items-center justify-center">
                  {activeStaff.avatarUrl ? (
                    <img
                      src={activeStaff.avatarUrl}
                      alt={activeStaff.name}
                      className="w-full h-full object-cover object-center"
                    />
                  ) : (
                    <div
                      style={{
                        backgroundColor: activeStaff.colorHex || theme.brandButtonBg || '#111827',
                        color: '#FFFFFF'
                      }}
                      className="w-full h-full flex items-center justify-center font-bold text-2xl sm:text-4xl uppercase select-none"
                    >
                      {activeStaff.name.charAt(0)}
                    </div>
                  )}
                </div>

                {/* Badge circular con el logo de la marca (sin contornos ni bordes, la imagen ocupa el 100% circular) */}
                <div className="absolute bottom-1.5 right-1.5 sm:bottom-2 sm:right-2 md:bottom-2.5 md:right-2.5 w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full shadow-md overflow-hidden flex items-center justify-center z-10">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={businessName}
                      className="w-full h-full object-cover object-center"
                    />
                  ) : (
                    <div
                      style={{
                        backgroundColor: theme.brandButtonBg || '#111827',
                        color: theme.brandButtonText || '#FFFFFF'
                      }}
                      className="w-full h-full flex items-center justify-center font-bold text-xs uppercase select-none"
                    >
                      {initialLetter}
                    </div>
                  )}
                </div>
              </div>

              {/* Nombre del profesional + Marca y Sede + Desarrollado con Calenddy.com */}
              <div className="min-w-0 flex-1 flex flex-col justify-center text-left py-1">
                <h1
                  style={{
                    fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif',
                    fontSize: '28px',
                    fontWeight: 600,
                    lineHeight: '34px',
                    color: '#111827'
                  }}
                  className="tracking-normal truncate"
                >
                  {activeStaff.name}
                </h1>
                <p
                  style={{
                    fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                    fontSize: '15px',
                    fontWeight: 400,
                    lineHeight: '22px',
                    color: '#6b7280'
                  }}
                  className="mt-0.5"
                >
                  {businessName} - {branchName}
                </p>
                <div className="flex items-center gap-1.5 mt-1 text-[13px] text-gray-400">
                  <span>Desarrollado por Calenddy.com</span>
                  <svg className="w-3.5 h-3.5 text-emerald-500 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Cabecera de Sede (Réplica exacta Imagen 1): Logo + Nombre Sede + Marca (Izquierda) | Reseñas + Horario + Ubicación (Derecha) */
          <div className="pt-4 sm:pt-6 pb-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Lado Izquierdo: Logo + Nombres */}
            <div className="flex items-center gap-4 sm:gap-6 min-w-0">
              <div className="shrink-0">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={businessName}
                    className="h-24 w-24 sm:h-32 sm:w-32 md:h-36 md:w-36 rounded-3xl object-cover object-center bg-black text-white ring-4 ring-white shadow-sm"
                  />
                ) : (
                  <div
                    style={{
                      backgroundColor: theme.brandButtonBg || '#111827',
                      color: theme.brandButtonText || '#FFFFFF'
                    }}
                    className="h-24 w-24 sm:h-32 sm:w-32 md:h-36 md:w-36 rounded-3xl ring-4 ring-white shadow-sm font-bold text-2xl sm:text-4xl flex items-center justify-center select-none"
                  >
                    {initialLetter}
                  </div>
                )}
              </div>

              {/* Nombre de Sede y Marca */}
              <div className="min-w-0 flex-1 flex flex-col justify-center text-left py-1">
                <h1
                  style={{
                    fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif',
                    fontSize: '28px',
                    fontWeight: 600,
                    lineHeight: '34px',
                    color: '#111827'
                  }}
                  className="tracking-normal truncate"
                >
                  {branchName}
                </h1>
                <p
                  style={{
                    fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                    fontSize: '15px',
                    fontWeight: 400,
                    lineHeight: '22px',
                    color: '#6b7280'
                  }}
                  className="mt-0.5"
                >
                  {businessName}
                </p>
              </div>
            </div>

            {/* Lado Derecho (Tarjeta Idéntica a Calendico Cerini Alcorta): Reseñas + Horario Abierto ahora + Ubicación */}
            <div className="mt-1 flex flex-col w-full sm:w-auto max-w-sm border border-gray-200/80 min-w-64 rounded-lg bg-white shadow-2xs shrink-0">
              {/* 1. Reseñas: 4.8 ★★★★☆ (17810) */}
              <div className="flex justify-between items-center gap-x-2 p-2">
                <div className="flex items-center gap-x-2">
                  <span
                    style={{
                      fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                      fontSize: '15px',
                      fontWeight: 600,
                      color: '#111827'
                    }}
                    className="ml-1"
                  >
                    4.8
                  </span>
                  <div className="flex gap-x-1 text-[#111827]">
                    <Star className="w-4 h-4 fill-[#111827] text-[#111827]" />
                    <Star className="w-4 h-4 fill-[#111827] text-[#111827]" />
                    <Star className="w-4 h-4 fill-[#111827] text-[#111827]" />
                    <Star className="w-4 h-4 fill-[#111827] text-[#111827]" />
                    <Star className="w-4 h-4 text-gray-300" />
                  </div>
                  <span
                    style={{
                      fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}
                  >
                    (17810)
                  </span>
                </div>
              </div>

              {/* 2. Horarios: ● Abierto ahora > */}
              <button
                type="button"
                onClick={() => setShowLocationModal(true)}
                className="flex justify-between items-center gap-x-2 p-2 hover:bg-gray-50/80 transition cursor-pointer group text-left w-full"
              >
                <div className="flex items-center gap-x-2">
                  <div className="relative h-2 w-2 block ml-1 shrink-0">
                    <span className="flex absolute h-2 w-2 top-0 right-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                  </div>
                  <span
                    style={{
                      fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#111827'
                    }}
                  >
                    Abierto ahora
                  </span>
                </div>
                <div className="flex items-center gap-x-2 group-hover:translate-x-0.5 transition-transform duration-200">
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </button>

              {/* 3. Ubicación: 📍 Cerini Alcorta  Ver ubicación (con separador border-t) */}
              <div className="flex items-center gap-x-2 p-2 pl-2 w-full text-sm border-t border-gray-100">
                <MapPin className="w-4 h-4 text-gray-400 shrink-0 ml-1" />
                <span
                  style={{
                    fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                    fontSize: '14px',
                    color: '#374151'
                  }}
                  className="inline"
                >
                  {businessName} {branchName}
                  <button
                    type="button"
                    onClick={() => setShowLocationModal(true)}
                    style={{
                      fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#111827'
                    }}
                    className="inline-flex underline ml-1.5 hover:text-black cursor-pointer font-medium"
                  >
                    Ver ubicación
                  </button>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* 4.A: Fila de Profesionales (Solo en vista general de la sucursal) */}
        {!activeStaff && staffList.length > 0 && (
          <section className="space-y-3">
            <h2
              style={{
                fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif',
                fontSize: '18px',
                fontWeight: 600,
                color: '#111827'
              }}
            >
              Profesionales
            </h2>

            <div className="flex items-center gap-3.5 sm:gap-4 overflow-x-auto pb-2 scrollbar-none pt-1">
              {staffList.map((st) => {
                const isSelected = selectedStaffId === st.id;
                // Solo muestra borde punteado y estrella si el comercio lo marcó como destacado en el admin
                const isFeatured = Boolean(st.isFeatured);
                const firstName = st.name.split(' ')[0];

                return (
                  <button
                    key={st.id}
                    onClick={() => handleSelectStaffMember(st.id)}
                    className="flex flex-col items-center min-w-[70px] sm:min-w-[80px] cursor-pointer group text-center focus:outline-hidden"
                  >
                    <div className="relative inline-block">
                      <div
                        className={`rounded-full transition-all ${
                          isSelected
                            ? 'p-0.5 border-2 border-[#111827]'
                            : isFeatured
                              ? 'p-0.5 border-2 border-dashed border-[#111827]'
                              : 'p-0.5 border-2 border-transparent'
                        }`}
                      >
                        {st.avatarUrl ? (
                          <img
                            src={st.avatarUrl}
                            alt={st.name}
                            className="w-16 h-16 sm:w-18 sm:h-18 rounded-full object-cover object-center bg-gray-100 group-hover:scale-105 transition-transform"
                          />
                        ) : (
                          <div
                            style={{
                              backgroundColor: st.colorHex || '#111827',
                              color: '#FFFFFF'
                            }}
                            className="w-16 h-16 sm:w-18 sm:h-18 rounded-full flex items-center justify-center font-bold text-lg group-hover:scale-105 transition-transform"
                          >
                            {firstName.charAt(0)}
                          </div>
                        )}
                      </div>

                      {/* Estrellita negra de destacado (solo para profesional destacado configurado por el comercio) */}
                      {isFeatured && (
                        <div className="absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full bg-[#111827] text-white flex items-center justify-center text-[9px] shadow-xs border border-white">
                          ★
                        </div>
                      )}
                    </div>

                    <span
                      style={{
                        fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                        fontSize: '14px',
                        fontWeight: 400,
                        color: '#111827'
                      }}
                      className="mt-1.5 truncate max-w-[75px]"
                    >
                      {firstName}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Pequeña barra indicadora de scroll (Réplica Captura 3) */}
            <div className="w-10 h-1 bg-gray-300 rounded-full mt-1 mb-2" />
          </section>
        )}

        {/* Título Servicios (Solo en vista general de la sucursal) */}
        {!activeStaff && (
          <h2
            style={{
              fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif',
              fontSize: '20px',
              fontWeight: 600,
              color: '#111827'
            }}
            className="-mt-3 sm:-mt-4 mb-4 sm:mb-5"
          >
            Servicios
          </h2>
        )}

        {/* 4.B: Buscador y Categorías Scrolleables (Sticky como en Captura 1 y 2) */}
        <section className="sticky top-[58px] z-30 bg-white py-2.5 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 border-b border-gray-100 space-y-2.5 shadow-2xs">
          {/* Input de Búsqueda Full-width (Solo en vista de sucursal/portada, NO en perfil de profesional) */}
          {!activeStaff && (
            <div className="relative w-full">
              <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar servicios..."
                style={{
                  fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                  fontSize: '14px'
                }}
                className="w-full pl-10 pr-3 py-2.5 rounded-2xl border border-gray-200 bg-white focus:outline-hidden focus:border-gray-400 transition-colors text-[#111827] placeholder:text-gray-400 shadow-2xs"
              />
            </div>
          )}

          {/* Category Pills (Solo categorías reales de la sucursal o del profesional) */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none pt-0.5">
            {displayCategories.map((cat) => {
              const isSelected = selectedCategoryId === cat.id || selectedCategoryId === cat.name;
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    setSelectedCategoryId(cat.id);
                    const el = document.getElementById(`category-${slugify(cat.name)}`);
                    if (el) {
                      const headerOffset = 135;
                      const elementPosition = el.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                    }
                  }}
                  style={{
                    fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '20px',
                    backgroundColor: isSelected ? '#111827' : '#FFFFFF',
                    color: isSelected ? '#FFFFFF' : '#111827',
                    borderColor: isSelected ? '#111827' : '#E5E7EB'
                  }}
                  className="whitespace-nowrap py-2 px-4 border rounded-full transition-colors cursor-pointer shadow-2xs hover:border-gray-400 shrink-0"
                >
                  {cat.name}
                </button>
              );
            })}
          </div>
        </section>

        {/* 4.C: Listado de Servicios */}
        {filteredServices.length === 0 ? (
          <div className="rounded-[16px] border border-gray-200 p-12 text-center max-w-md mx-auto bg-white">
            <Search className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <h3
              style={{
                fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif',
                fontSize: '16px',
                fontWeight: 500,
                color: '#111827'
              }}
              className="mb-1"
            >
              No se encontraron servicios
            </h3>
            <p
              style={{
                fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                fontSize: '13px',
                color: '#6b7280'
              }}
            >
              Probá ajustando la búsqueda o seleccionando otra categoría.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* SERVICIO DESTACADO: Se muestra en la vista de la sucursal como producto/servicio destacado */}
            {!activeStaff && featuredService && !searchQuery.trim() && (
              <div className="space-y-4 pt-1">
                <div>
                  <h3
                    style={{
                      fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif',
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#111827'
                    }}
                    className="mb-3"
                  >
                    Servicio Destacado
                  </h3>

                  {/* Tarjeta Negra Destacada (Reducida un 30% más, aprox. 390px de ancho) */}
                  <div
                    onClick={() => onSelectService(featuredService)}
                    className="w-full max-w-[390px] bg-[#18181b] hover:bg-[#27272a] text-white rounded-2xl p-4 shadow-sm flex items-center justify-between gap-3 cursor-pointer transition group"
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <p
                        style={{
                          fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                          fontSize: '16px',
                          fontWeight: 500,
                          lineHeight: '22px'
                        }}
                        className="text-white break-words line-clamp-1"
                      >
                        {featuredService.name}
                      </p>
                      <p
                        style={{
                          fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                          fontSize: '13px',
                          lineHeight: '18px'
                        }}
                        className="text-gray-400 mt-0.5 line-clamp-1"
                      >
                        {featuredService.description || 'Exclusivo clientes'}
                      </p>
                      <div
                        style={{
                          fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif'
                        }}
                        className="flex items-center gap-1.5 mt-2"
                      >
                        <span className="text-[15px] font-semibold text-white">
                          {formatPrice(featuredService)}
                        </span>
                        <span className="text-gray-500 mx-1">|</span>
                        <span className="text-[13px] text-gray-400">
                          {formatDuration(featuredService.duration)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-center shrink-0">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectService(featuredService);
                        }}
                        className="w-9 h-9 rounded-xl bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition cursor-pointer"
                        aria-label={`Reservar ${featuredService.name}`}
                      >
                        <Plus className="w-5 h-5 stroke-[2.5]" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Sección "Recomendado para ti" */}
                {recommendedServices.length > 0 && (
                  <div className="pt-3">
                    <div className="flex items-center gap-1.5 text-[17px] font-semibold text-[#111827] mb-2 sm:mb-2.5">
                      <Sparkles className="w-4 h-4 text-[#111827]" />
                      <span
                        style={{
                          fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif'
                        }}
                      >
                        Recomendado para ti
                      </span>
                    </div>

                    <div className="flex items-center gap-3 overflow-x-auto pb-3 pt-3.5 px-0.5 scrollbar-none">
                      {/* Tarjeta 1: Con Píldora Negra "MÁS ELEGIDO" y borde punteado */}
                      {recommendedServices[0] && (
                        <div
                          onClick={() => onSelectService(recommendedServices[0])}
                          className="relative border-2 border-dashed border-[#111827] rounded-2xl p-3.5 bg-white min-w-[210px] sm:min-w-[240px] flex items-center justify-between gap-3 shadow-2xs shrink-0 cursor-pointer hover:bg-gray-50/60 transition group"
                        >
                          <span className="absolute -top-3 left-4 bg-[#111827] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider shadow-xs select-none">
                            MÁS ELEGIDO
                          </span>
                          <div className="min-w-0 flex-1">
                            <p
                              style={{
                                fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                                fontSize: '15px',
                                fontWeight: 500,
                                color: '#111827'
                              }}
                              className="truncate"
                            >
                              {recommendedServices[0].name}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectService(recommendedServices[0]);
                            }}
                            className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-800 transition cursor-pointer shrink-0"
                          >
                            <Plus className="w-4 h-4 stroke-[2.5]" />
                          </button>
                        </div>
                      )}

                      {/* Tarjeta 2 */}
                      {recommendedServices[1] && (
                        <div
                          onClick={() => onSelectService(recommendedServices[1])}
                          className="border border-gray-200 rounded-2xl p-3.5 bg-white min-w-[190px] sm:min-w-[220px] flex items-center justify-between gap-3 shadow-2xs shrink-0 cursor-pointer hover:border-gray-300 transition group"
                        >
                          <div className="min-w-0 flex-1">
                            <p
                              style={{
                                fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                                fontSize: '15px',
                                fontWeight: 500,
                                color: '#111827'
                              }}
                              className="truncate"
                            >
                              {recommendedServices[1].name}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectService(recommendedServices[1]);
                            }}
                            className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-800 transition cursor-pointer shrink-0"
                          >
                            <Plus className="w-4 h-4 stroke-[2.5]" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* SECCIONES POR CATEGORÍA: 3 columnas en desktop como en las capturas */}
            {Object.entries(groupedServices).map(([categoryName, catServices]) => (
              <div
                key={categoryName}
                id={`category-${slugify(categoryName)}`}
                className="space-y-3 scroll-mt-36 pt-1"
              >
                <h3
                  style={{
                    fontFamily: '"Plus Jakarta Display", "Inter var", ui-sans-serif, system-ui, sans-serif',
                    fontSize: '18px',
                    fontWeight: 600,
                    lineHeight: '26px',
                    color: '#111827'
                  }}
                  className="py-1"
                >
                  {categoryName}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4">
                  {catServices.map((service) => {
                    const priceFormatted = formatPrice(service);
                    const durationFormatted = formatDuration(service.duration);

                    return (
                      <div
                        key={service.id}
                        onClick={() => onSelectService(service)}
                        className="p-4 border border-gray-200 rounded-2xl bg-white hover:border-gray-300 transition-all duration-150 flex items-center justify-between gap-3 cursor-pointer group shadow-2xs"
                      >
                        <div className="flex flex-col min-w-0 flex-1">
                          <p
                            style={{
                              fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                              fontSize: '16px',
                              fontWeight: 500,
                              lineHeight: '22px',
                              color: '#111827'
                            }}
                            className="break-words line-clamp-2"
                          >
                            {service.name}
                          </p>

                          {service.description && (
                            <p
                              style={{
                                fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif',
                                fontSize: '13px',
                                lineHeight: '18px',
                                color: '#6b7280'
                              }}
                              className="mt-0.5 line-clamp-1"
                            >
                              {service.description}
                            </p>
                          )}

                          <div
                            style={{
                              fontFamily: '"Plus Jakarta Display", var(--font-plus-jakarta), "Inter var", sans-serif'
                            }}
                            className="flex items-center gap-1.5 mt-2"
                          >
                            <span
                              style={{
                                fontSize: '15px',
                                fontWeight: 600,
                                color: '#111827'
                              }}
                            >
                              {priceFormatted}
                            </span>
                            <span className="text-gray-300 mx-1">|</span>
                            <span
                              style={{
                                fontSize: '13px',
                                fontWeight: 400,
                                color: '#6b7280'
                              }}
                            >
                              {durationFormatted}
                            </span>
                          </div>
                        </div>

                        {/* Botón Rounded + con fondo gris claro idéntico a Capturas */}
                        <div className="flex items-center justify-center shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectService(service);
                            }}
                            className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-[#111827] transition-all group-hover:scale-105 cursor-pointer shadow-2xs"
                            aria-label={`Reservar ${service.name}`}
                          >
                            <Plus className="w-5 h-5 stroke-[2.5]" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Modal de Ubicación y Horarios (se activa al clickear "Ver ubicación" o "Abierto ahora") */}
      {showLocationModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40 backdrop-blur-xs animate-in fade-in duration-200">
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl p-6 shadow-2xl space-y-4 animate-in slide-in-from-bottom sm:zoom-in-95 duration-200"
          >
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div>
                <h3 className="text-lg font-semibold text-[#111827]">{branchName}</h3>
                <p className="text-xs text-gray-500">{businessName}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowLocationModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-sm text-[#111827]">
              {currentBranch?.address && (
                <div className="flex items-start gap-2.5">
                  <MapPin className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#111827]">Dirección</p>
                    <p className="text-gray-600 text-xs sm:text-sm mt-0.5">{currentBranch.address}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2.5">
                <Clock className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-[#111827]">Horarios de atención</p>
                  <p className="text-gray-600 text-xs sm:text-sm mt-0.5">Lunes a Sábados: 09:00 - 20:00 hs</p>
                </div>
              </div>

              {currentBranch?.phone && (
                <div className="flex items-start gap-2.5">
                  <Phone className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#111827]">Teléfono</p>
                    <p className="text-gray-600 text-xs sm:text-sm mt-0.5">{currentBranch.phone}</p>
                  </div>
                </div>
              )}
            </div>

            {currentBranch?.googleMapsUrl && (
              <a
                href={currentBranch.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 px-4 rounded-xl bg-[#111827] hover:bg-black text-white flex items-center justify-center gap-2 text-sm font-medium transition cursor-pointer mt-2"
              >
                <span>Abrir en Google Maps</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* 5. Footer Discreto */}
      <footer className="w-full border-t border-gray-100 bg-white py-6 px-4 text-center text-xs text-gray-500 mt-auto">
        <div className="max-w-[1310px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-6">
          <p>© {new Date().getFullYear()} {branchName} · {businessName}</p>
          <div className="flex items-center gap-1.5">
            <span>Powered by</span>
            <CalenddyLogo size="xs" variant="dark" />
          </div>
        </div>
      </footer>
    </div>
  );
}
